import type { CasoDetalle } from "@/lib/api";
import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";

/**
 * Ayuda del encabezado del caso (botón «Ayuda» junto a las acciones). Lo que
 * antes iba en los tooltips de cada botón, de las fichas de ticket y de las
 * pestañas. Cada pestaña trae SU propia Ayuda: aquí no se repite.
 *
 * Los botones que solo existen en ciertos casos (cerrado, con ticket, con
 * permiso de edición) se listan con la MISMA condición que usa
 * `pages/CasoDetalle`: un item de un botón que no ves no ayuda. Si cambias una
 * condición allá, cámbiala aquí.
 */
export function ayudaCasoDetalle({
  caso,
  puedeEditar,
  hayRecorrido,
}: {
  caso: CasoDetalle;
  /** `canEdit(me)`: admin o soporte. */
  puedeEditar: boolean;
  /** Hay lista de referencia (se llegó desde Casos): salen Anterior/Siguiente. */
  hayRecorrido: boolean;
}): ContenidoAyuda {
  const abierto = caso.estado !== "Cerrado";
  const editaAbierto = puedeEditar && abierto;
  const esJimi = !!caso.marca?.toLowerCase().includes("jimi");
  const esQueclink = !!caso.marca?.toLowerCase().includes("queclink");
  const conQC = editaAbierto && esQueclink;
  const si = (cond: boolean, item: ItemAyuda): ItemAyuda[] => (cond ? [item] : []);

  const botones: ItemAyuda[] = [
    { label: "← Casos", text: "Te regresa a la lista de casos." },
    ...si(hayRecorrido, {
      label: "‹ Anterior / Siguiente ›",
      text: "Pasan al caso de antes o de después en tu lista, con el mismo orden y filtros que tenías; «3 de 20» es tu lugar en ella. En el celular también puedes deslizar el dedo a los lados. Si entras por un enlace directo (una alerta, un TE) no aparecen.",
    }),
    {
      label: "Folio, TE y número de ticket",
      text: "Tócalos para copiarlos, sin seleccionarlos a mano: sale una ✓ y un aviso de «copiado».",
    },
    { label: "Nombre del cliente", text: "Te lleva a la lista de casos." },
    ...si(!!caso.jimi_work_order_no && caso.jimi_origen === "manual" && puedeEditar, {
      label: "✏️ (junto al ticket de Jimi)",
      text: "Ese número se capturó a mano: tócalo para corregirlo si quedó mal escrito.",
    }),
    ...si(puedeEditar, {
      label: caso.modelos.length > 1 ? "✎ Editar (modelos)" : "✎ Editar modelos",
      text: "Cambia la marca y el modelo del caso, o agrega más si trae equipos de varios modelos. El primero de la lista es el principal.",
    }),
    ...si(puedeEditar, {
      label: caso.asesor_comercial ? "✎ Cambiar (asesor)" : "✎ Asignar (asesor)",
      text: "Elige el asesor comercial sin salir del caso. Ojo: el asesor es de la empresa, no del caso, así que el cambio se ve en todos los casos de ese cliente.",
    }),
    ...si(editaAbierto, {
      label: "☆ / ★ (junto a un contacto)",
      text: "★ marca al contacto principal, el que recibe los correos (Para). Tocar ☆ en otro lo vuelve principal y el anterior pasa a seguimiento solo; tocar ★ lo deja como seguimiento.",
    }),
    ...si(editaAbierto, {
      label: "✎ (junto a un contacto)",
      text: "Corrige su nombre, correo o teléfono. El cambio es en la ficha del cliente: se ve en todos sus casos.",
    }),
    ...si(editaAbierto, {
      label: "✕ (junto a un contacto)",
      text: "Lo quita de este caso, después de confirmar. No lo borra de la ficha del cliente. Si quitas al principal, no se puede mandar correo al cliente hasta que elijas otro.",
    }),
    ...si(editaAbierto, {
      label: "+ agregar contacto",
      text: "Suma al caso un contacto que ya tiene el cliente, o da de alta uno nuevo, como principal o de seguimiento.",
    }),
    ...si(!!caso.proveedor_respondio_at && puedeEditar, {
      label: "⏳ el proveedor contestó",
      text: "Jimi o Queclink contestó en su ticket y todavía no le respondemos. Si ya lo resolviste por otro lado (garantía, reemplazo, el cliente desistió), tócalo para darlo por atendido; puedes anotar por qué y queda en la bitácora. Responderle desde el caso o cerrar el caso también la quitan.",
    }),
    ...si(editaAbierto, {
      label: "Estado (selector)",
      text: "Cambia el estado del caso y queda en la bitácora. «Cerrado» no está aquí: para cerrar usa «Cerrar caso».",
    }),
    ...si(editaAbierto, {
      label: "Asignado (selector)",
      text: "Asigna el caso a un compañero; le llega un aviso (si te lo asignas tú, no).",
    }),
    ...si(editaAbierto && caso.imeis.length === 0, {
      label: "Agregar IMEI",
      text: "Registra los equipos del caso; puedes pegar varios IMEIs de un jalón. Toman el modelo del caso. Solo sale mientras el caso no tiene ninguno; después se agregan en la pestaña IMEIs.",
    }),
    ...si(editaAbierto, {
      label: "Notificar",
      text: "Le pasas el caso a un compañero con un mensaje. Le llega a sus Alertas (y a Telegram o al celular si los tiene activos) y, si tiene prendido el aviso por correo, un correo desde tu correo empresarial (si ya lo configuraste). Queda en la bitácora y, si lo eliges, también se le asigna el caso.",
    }),
    ...si(editaAbierto, {
      label: "Cerrar caso",
      text: "Cierra el caso. Pide cómo se resolvió (el tipo de solución; con varios equipos puede ser una distinta por equipo) y una nota de cierre opcional; el cierre queda en la bitácora y se avisa a quien abrió el caso (si no fuiste tú). Los ZIP de Evidencias se borran 12 meses después del cierre.",
    }),
    ...si(puedeEditar && !caso.duplicado_de_id, {
      label: "Duplicado",
      text: abierto
        ? "Marca este caso como duplicado de otro: te sugiere los que comparten IMEI, o lo buscas por folio o TE. Este caso se cierra como «Duplicado» y queda ligado al original. Se deshace con «Quitar marca»."
        : "Liga este caso cerrado como duplicado de otro (te sugiere los que comparten IMEI, o lo buscas por folio o TE). Como ya está cerrado, solo se relaciona: su cierre no cambia.",
    }),
    ...si(editaAbierto && esJimi && !caso.jimi_work_order_no, {
      label: "Ticket en Jimi",
      text:
        caso.imeis.length > 0
          ? "Abre el ticket de este caso en el sistema de Jimi. Si ya lo abriste allá a mano, usa «Ya tengo el número» para registrarlo."
          : "Este caso no tiene IMEIs, y desde aquí el ticket se arma con los equipos del caso, así que no se puede crear. Ábrelo a mano en el portal de Jimi y registra aquí su número para que el caso lo siga.",
    }),
    ...si(editaAbierto && !!caso.jimi_work_order_no, {
      label: "Revisar en Jimi",
      text: "Trae lo que Jimi haya contestado y lo pone en la bitácora (los archivos que manden se guardan en Evidencias); también actualiza el estado del ticket. Si contestaron, el caso queda marcado «⏳ el proveedor contestó» y se avisa al asignado.",
    }),
    ...si(editaAbierto && !!caso.jimi_work_order_no, {
      label: "Responder a Jimi",
      text: "Le contestas al técnico de Jimi en su ticket sin salir del caso. Ojo: Jimi no deja borrar ni editar una respuesta. Queda en la bitácora y, si el caso estaba en un estado donde aplica, pasa a «Esperando proveedor».",
    }),
    ...si(conQC && !!caso.queclink_ticket_id, {
      label: "Revisar en Queclink",
      text: "Trae lo que Queclink haya contestado y lo pone en la bitácora; también actualiza el estado del ticket.",
    }),
    ...si(conQC && !!caso.queclink_ticket_id && !caso.queclink_ticket_cerrado, {
      label: "Responder a Queclink",
      text: "Le contestas a Queclink en su ticket sin salir del caso; no se puede borrar después. Queda en la bitácora y, si el caso estaba en un estado donde aplica, pasa a «Esperando proveedor».",
    }),
    ...si(conQC && !!caso.queclink_ticket_id && !!caso.queclink_ticket_cerrado, {
      label: "Ticket de seguimiento",
      text: "Queclink ya cerró el ticket y no acepta respuestas. Esto abre uno nuevo ligado al cerrado, con tu mensaje, y el caso sigue a partir de ahí con el nuevo. No se puede borrar después.",
    }),
    ...si(conQC && !caso.queclink_ticket_id, {
      label: "Ticket en Queclink",
      text: "Abre un ticket nuevo en el portal de Queclink desde este caso. Si ya existe uno, usa «Ya tengo el número» para ligarlo.",
    }),
    ...si(conQC && !caso.queclink_ticket_id && !!caso.ticket_externo, {
      label: "Buscar ticket",
      text: "Busca en Queclink un ticket que traiga el TE de este caso en el asunto y lo liga solo. Si no aparece, ábrelo o lígalo con «Ticket en Queclink».",
    }),
    ...si(puedeEditar && !abierto, {
      label: "+ Caso relacionado",
      text: "Si el cliente vuelve con el mismo equipo: abre un caso nuevo con el mismo cliente, marca, modelo e IMEIs ya puestos (los puedes cambiar), ligado a este en el «Hilo del caso». El cerrado no se reabre y tú te quedas aquí.",
    }),
    ...si(!!caso.duplicado_de_id && puedeEditar, {
      label: "Quitar marca",
      text: "Deshace la marca de duplicado. Si lo habíamos cerrado como duplicado, el caso se vuelve a abrir.",
    }),
    ...si(!!caso.fecha_envio_proveedor && !caso.fecha_retorno_proveedor && puedeEditar, {
      label: "Marcar retorno hoy",
      text: "Registra que el equipo regresó del proveedor hoy: el caso pasa a «En revision», los equipos a REV (los CRIT se respetan) y se programa un recordatorio para devolverlo al cliente. Para otra fecha, ajústala en «Hitos del caso».",
    }),
    ...si(caso.imeis.length > 0, {
      label: "▾ Equipos (N)",
      text: "Los IMEIs del caso a la vista mientras escribes en la bitácora. Toca un equipo para copiar su IMEI; ▾ oculta la franja y ▸ la vuelve a mostrar (se recuerda en todos los casos).",
    }),
  ];

  const indicadores: ItemAyuda[] = [
    {
      label: "Estado (ficha de color)",
      text: "En qué va el caso: Abierto, En revision, Listo para enviar, Enviado al proveedor, Esperando proveedor, Pendiente cliente o Cerrado.",
    },
    ...si(!!caso.jimi_work_order_no, {
      label: "🎫 Jimi · número · estado",
      text: "El ticket del caso en el sistema de Jimi. Después del punto va su estado allá: Recibido, sin atender · En atención · Concluido por Jimi · Suspendido por Jimi. Se actualiza cada vez que se revisa el ticket.",
    }),
    ...si(!!caso.queclink_ticket_id, {
      label: "🎫 Queclink · número · estado",
      text: "El ticket del caso en el portal de soporte de Queclink. Después del punto va su estado allá: Nuevo · Abierto · Esperan nuestra respuesta · En espera · Resuelto (contestarle todavía lo reabre) · Cerrado (ya no acepta respuestas). Se actualiza cada vez que se revisa el ticket.",
    }),
    ...si(caso.modelos.length > 1, {
      label: "PRINCIPAL · (en un modelo)",
      text: "El modelo principal del caso, cuando trae equipos de varios modelos.",
    }),
    ...si(caso.contactos.length > 0, {
      label: "PRINCIPAL / SEGUIMIENTO (en un contacto)",
      text: "El principal recibe el correo al cliente (Para); los de seguimiento van en copia. Debajo de cada contacto dice quién lo agregó al caso y cuándo.",
    }),
    ...si(caso.contactos.some((c) => !c.activo), {
      label: "ARCHIVADO",
      text: "El contacto se archivó en la ficha del cliente. Sigue aquí porque ya estaba en el caso.",
    }),
    ...si(!!caso.proveedor_respondio_at && !puedeEditar, {
      label: "⏳ el proveedor contestó",
      text: "Jimi o Queclink contestó en su ticket y todavía no le respondemos.",
    }),
    ...si(caso.imeis.length > 0, {
      label: "Chips de Equipos",
      text: "Cada IMEI con el color de su estado: OK verde, REV ámbar, CRIT rojo, CLI azul, PROVEEDOR naranja. El detalle está en la pestaña IMEIs.",
    }),
    ...si(!!caso.duplicado_de_id, {
      label: "🔗 Duplicado de …",
      text: "Este caso se marcó como duplicado de otro. Toca el folio para ir al original.",
    }),
    ...si(!!caso.fecha_envio_proveedor && !caso.fecha_retorno_proveedor, {
      label: "Pendiente: el equipo se envió al proveedor…",
      text: "El equipo sigue con el proveedor; cuenta los días desde que se envió hasta que registres su regreso.",
    }),
  ];

  return {
    manual: "detalle",
    intro: (
      <p>
        Todo sobre un caso. Arriba, quién es el cliente, en qué va y las acciones; abajo, las
        pestañas. Cada pestaña tiene su propio botón <b>Ayuda</b>.
      </p>
    ),
    secciones: [
      { titulo: "Qué hace cada botón", items: botones },
      { titulo: "Qué significa cada indicador", items: indicadores },
      {
        titulo: "Las pestañas",
        items: [
          {
            label: "Bitácora interna",
            text: "El seguimiento del caso: notas, llamadas y lo que el sistema registra solo. SOLO la ve el equipo, NO se manda al cliente. El número cuenta las notas, incluidas las que escribe el sistema.",
          },
          {
            label: "IMEIs",
            text: "Los equipos del caso con su estado de revisión. El número es cuántos hay.",
          },
          {
            label: "Evidencias",
            text: "Los archivos del caso (fotos, videos, PDFs…) y los paquetes ZIP para el proveedor.",
          },
          {
            label: "Correos",
            text: "Los correos enviados y recibidos del caso. Desde aquí le escribes al cliente o al proveedor: el mensaje al cliente se guarda solo como borrador mientras escribes, puedes reusar borradores guardados y sale con la plantilla de la empresa.",
          },
          {
            label: "Recordatorios",
            text: "Los avisos programados del caso. Al vencer llegan a Alertas, y por correo, Telegram o al celular según lo que cada quien tenga activo.",
          },
        ],
      },
      {
        titulo: "Atajos de teclado",
        items: si(hayRecorrido, {
          label: "Ctrl + ← / Ctrl + →",
          text: "Caso anterior o siguiente de tu lista. No funcionan mientras escribes en un campo ni con una ventana o el visor de archivos abiertos (ahí las flechas solas cambian de archivo).",
        }),
      },
    ],
  };
}
